import {ChangeDetectionStrategy, Component, DestroyRef, inject} from '@angular/core';
import {AggregationLevelPipe} from 'src/app/shared/pipes/aggregation-level.pipe';
import {FormControl, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {LegendMetric} from 'src/app/shared/enums/legend-metric.enum';
import {GenOutageAggregationLevel} from 'generated/serverModels/GenOutageAggregationLevel';
import {LegendNamePipe} from '../../pipes/legend-name.pipe';
import {UserService} from '../../services/user.service';
import {takeUntilDestroyed, toSignal} from '@angular/core/rxjs-interop';
import {LegendMetricPipe} from 'src/app/shared/pipes/legend-metric.pipe';
import {tap} from 'rxjs';
import {AggregationLevel, LocationData, VulcanLocationFilterComponent} from '@eagle-i/vulcan-component-library';
import {DataService} from 'src/app/shared/services/data.service';
import {LegendStyle} from '../../classes/legend-style';
import {MatFormField, MatLabel} from '@angular/material/form-field';
import {MatOption, MatSelect} from '@angular/material/select';
import {NomLegendComponent} from 'src/app/shared/components/nom-legend/nom-legend.component';
import {MatButton} from '@angular/material/button';
import {GenStatePreference} from 'generated/serverModels/GenStatePreference';
import {UserPreference} from 'src/app/shared/classes/user-preferences';
import {VulcanNotifyService} from '@eagle-i/vulcan-component-library';
import {AuthenticationService} from 'src/app/shared/services/authentication.service';
import {ApplicationConfig} from 'src/app/shared/classes/application-config';
import {Router} from '@angular/router';
import {GlobalConfigService} from 'src/app/shared/services/global-config.service';
import {GlobalTimesliderService} from 'src/app/shared/components/global-timeslider/global-timeslider.service';

@Component({
  selector: 'eaglei-user-preference',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AggregationLevelPipe,
    ReactiveFormsModule,
    LegendNamePipe,
    LegendMetricPipe,
    VulcanLocationFilterComponent,
    MatFormField,
    MatSelect,
    MatOption,
    NomLegendComponent,
    MatLabel,
    MatButton,
  ],
  templateUrl: './user-preference.component.html',
  styleUrl: './user-preference.component.scss',
  standalone: true
})
export class UserPreferenceComponent {
    private readonly userService = inject(UserService);
    private readonly authService = inject(AuthenticationService);
    private readonly timesliderService = inject(GlobalTimesliderService);
    private readonly snackBar = inject(VulcanNotifyService);
    private readonly router = inject(Router);
    private readonly globalConfigService = inject(GlobalConfigService);
    private readonly destroyRef = inject(DestroyRef);

    private readonly user = toSignal(this.authService.authenticatedUser);

    protected readonly preferencesGroup = new FormGroup({
        aggregationLevel: new FormControl<GenOutageAggregationLevel>(GenOutageAggregationLevel.state, {
            nonNullable: true,
        }),
        states: new FormControl<string[] | null>(null),
        legendMetric: new FormControl<number>(LegendMetric.CUSTOMERS_OUT, {nonNullable: true}),
        legendStyle: new FormControl<LegendStyle>(undefined),
    });

    protected readonly aggregationLevels = [GenOutageAggregationLevel.state, GenOutageAggregationLevel.county];
    protected readonly legendStyles = toSignal(
        this.userService.getLegendOptions().pipe(
            tap((styles) => {
                this.preferencesGroup.controls.legendStyle.setValue(styles[0]);
            }),
        ),
        {initialValue: [] as LegendStyle[]},
    );

    protected readonly legendMetrics = LegendMetric.values();

    protected readonly defaultStateCodes = DataService.states.getValue().map((state) => {
        return state.geoid;
    });

    protected styleCompare(o1: LegendStyle, o2: LegendStyle): boolean {
        return o1.id === o2.id;
    }

    protected updateSelectedLocation(event: LocationData): void {
        this.preferencesGroup.patchValue({
            states: event.stateCodes,
        });
    }

    protected savePreferences(): void {
        const preferences = new UserPreference();
        preferences.userId = this.user()?.id;
        preferences.setAggregationLevel(this.preferencesGroup.controls.aggregationLevel.value);

        preferences.legendStyle = this.preferencesGroup.controls.legendStyle.value;
        preferences.legendMetric = this.preferencesGroup.controls.legendMetric.value as number;

        const selectedStateCodes = this.preferencesGroup.controls.states.value ?? [];
        const selectedStates = DataService.states
            .getValue()
            .filter((state) => selectedStateCodes.includes(state.geoid));
        preferences.states = selectedStates.map((state) => {
            const preferenceState = new GenStatePreference();
            preferenceState.id = state.id;
            preferenceState.name = state.name;
            preferenceState.abbreviation = state.abbreviation;
            preferenceState.stateAbbreviation = state.abbreviation;
            return preferenceState;
        });

        this.userService.savePreferences(preferences).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
            next: () => {
                this.snackBar.open('Preferences updated.', {tone: 'success', duration: 5000});
                ApplicationConfig.currentUserPreferences.next(preferences);

                this.globalConfigService.selectedLegendMetric.update(() => {
                    return LegendMetric.from(preferences.legendMetric);
                });

                const stateCodes = selectedStates.map((state) => state.geoid);
                this.timesliderService.setLocationAndAggregationSelection({
                    aggregationLevel: preferences.getOutageAggregationLevel().toString() as AggregationLevel,
                    stateCodes,
                    stateAbbreviations: selectedStates.map((state) => state.abbreviation),
                    countyCodes: DataService.getCountiesFromStateIds(stateCodes).map((county) => county.geoid),
                });

                this.router.navigateByUrl('/app/landing');
            },
            error: (error: {error?: {userMessage?: string}}) => {
                const text = error.error?.userMessage ?? 'Failed to update preferences.';
                this.snackBar.open(text, {tone: 'failure', duration: 5000});
            },
        });
    }
}
